import React, { useState, useRef, useEffect } from 'react';
import { authAPI, setToken } from './resellerapi';
import './ResellerPortal.css';

const ResellerAuth = ({ onAuthSuccess }) => {
  const [mode, setMode]           = useState('login');
  const [step, setStep]           = useState('mobile');
  const [mobile, setMobile]       = useState('');
  const [name, setName]           = useState('');
  const [otp, setOtp]             = useState(['', '', '', '', '', '']);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [devOtp, setDevOtp]       = useState('');   // shows OTP from API until SMS is live
  const [resendTimer, setResendTimer] = useState(0);
  const otpRefs = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    if (resendTimer > 0) {
      timerRef.current = setTimeout(() => setResendTimer(t => t - 1), 1000);
    }
    return () => clearTimeout(timerRef.current);
  }, [resendTimer]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(mobile)) { setError('Enter a valid 10-digit mobile number.'); return; }
    setError(''); setLoading(true);
    try {
      const res = await authAPI.sendOtp(mobile);
      // dev_otp returned from backend until SMS gateway configured
      if (res.dev_otp) {
        setDevOtp(res.dev_otp);
        // Auto-fill OTP boxes for easy testing
        const digits = res.dev_otp.toString().split('');
        setOtp(digits);
      }
      setStep('otp');
      setResendTimer(30);
      setTimeout(() => otpRefs.current[5]?.focus(), 100);
    } catch (err) {
      setError(err.message || 'Server error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (idx, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...otp];
    next[idx] = val;
    setOtp(next);
    if (val && idx < 5) otpRefs.current[idx + 1]?.focus();
    if (!val && idx > 0) otpRefs.current[idx - 1]?.focus();
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length < 6) { setError('Enter all 6 digits.'); return; }
    setError(''); setLoading(true);
    try {
      const res = await authAPI.verifyOtp(mobile, code, mode === 'register' ? name : null);
      setToken(res.token);
      localStorage.setItem('reseller_user', JSON.stringify(res.user));
      onAuthSuccess(res.user);
    } catch (err) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setError(''); setDevOtp(''); setLoading(true);
    try {
      const res = await authAPI.sendOtp(mobile);
      if (res.dev_otp) {
        setDevOtp(res.dev_otp);
        setOtp(res.dev_otp.toString().split(''));
      } else {
        setOtp(['', '', '', '', '', '']);
      }
      setResendTimer(30);
      otpRefs.current[0]?.focus();
    } catch (err) { setError(err.message || 'Server error.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="res-auth-root">
      {/* ── LEFT VISUAL ── */}
      <div className="res-auth-visual">
        <div className="auth-visual-content">
          <div className="auth-brand-mark">
            <div className="brand-hex">A</div>
            <span className="brand-name">ATR RESELLER HUB</span>
          </div>

          <h1 className="auth-headline">
            Earn up to <br />
            <span className="em">₹25,000/month</span><br />
            from home.
          </h1>
          <p className="auth-sub">
            Join 1,200+ resellers across Tamil Nadu selling ATR Agro products.
            Zero investment. Weekly payouts. Full support.
          </p>

          <div className="auth-stat-row">
            <div className="auth-stat-box">
              <span className="auth-stat-val">₹0</span>
              <span className="auth-stat-lbl">Investment</span>
            </div>
            <div className="auth-stat-box">
              <span className="auth-stat-val">1200+</span>
              <span className="auth-stat-lbl">Active Partners</span>
            </div>
            <div className="auth-stat-box">
              <span className="auth-stat-val">Weekly</span>
              <span className="auth-stat-lbl">Payouts</span>
            </div>
          </div>

          <div className="auth-benefits">
            {[
              'Share products via WhatsApp & Instagram',
              'We handle shipping & delivery for you',
              'Dedicated support manager assigned',
              'Up to 30% commission on every sale',
            ].map((b, i) => (
              <div key={i} className="auth-benefit-item">
                <div className="benefit-check">✓</div>
                <span>{b}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── RIGHT FORM ── */}
      <div className="res-auth-form-panel">
        <div className="res-form-card">
          <h2 className="res-form-title">
            {step === 'otp'
              ? 'Verify your number'
              : mode === 'login' ? 'Welcome back' : 'Start earning today'}
          </h2>
          <p className="res-form-sub">
            {step === 'otp'
              ? `OTP sent to +91 ${mobile}`
              : mode === 'login' ? 'Log in to your reseller account' : 'Create your account in 60 seconds'}
          </p>

          {error && <div className="res-error">{error}</div>}

          {/* Dev OTP hint banner */}
          {step === 'otp' && devOtp && (
            <div style={{
              background: '#0f2',
              color: '#000',
              padding: '8px 14px',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 700,
              marginBottom: '12px',
              textAlign: 'center',
              letterSpacing: '2px'
            }}>
              📩 OTP: {devOtp} &nbsp;(auto-filled)
            </div>
          )}

          <form onSubmit={step === 'mobile' ? handleSendOtp : handleVerify}>
            {/* REGISTER NAME */}
            {mode === 'register' && step === 'mobile' && (
              <div className="res-input-group">
                <label className="res-input-label">FULL NAME</label>
                <input
                  className="res-input"
                  type="text"
                  placeholder="Your full name"
                  required
                  value={name}
                  onChange={e => setName(e.target.value)}
                />
              </div>
            )}

            {/* MOBILE */}
            {step === 'mobile' && (
              <div className="res-input-group">
                <label className="res-input-label">MOBILE NUMBER</label>
                <div className="mobile-input-wrap">
                  <span className="mobile-prefix">+91</span>
                  <input
                    type="tel"
                    maxLength={10}
                    placeholder="10-digit number"
                    required
                    value={mobile}
                    onChange={e => setMobile(e.target.value.replace(/\D/g, ''))}
                  />
                </div>
              </div>
            )}

            {/* OTP */}
            {step === 'otp' && (
              <div className="res-input-group">
                <label className="res-input-label">ENTER 6-DIGIT OTP</label>
                <div className="otp-boxes">
                  {otp.map((d, i) => (
                    <input
                      key={i}
                      ref={el => (otpRefs.current[i] = el)}
                      className="otp-box"
                      type="tel"
                      maxLength={1}
                      value={d}
                      onChange={e => handleOtpChange(i, e.target.value)}
                      onKeyDown={e => handleOtpKeyDown(i, e)}
                    />
                  ))}
                </div>
                <div className="resend-timer">
                  {resendTimer > 0
                    ? `Resend in ${resendTimer}s`
                    : <span onClick={handleResend} style={{cursor:'pointer',color:'#00D084'}}>Resend OTP</span>}
                </div>
              </div>
            )}

            <button className="res-submit-btn" type="submit" disabled={loading}>
              {loading ? 'Please wait...' : step === 'mobile'
                ? 'Send OTP →'
                : mode === 'register' ? 'Verify & Start Earning' : 'Verify & Login'}
            </button>
          </form>

          {step === 'mobile' && (
            <p className="res-form-toggle">
              {mode === 'login'
                ? <>New reseller? <span onClick={() => { setMode('register'); setError(''); }}>Join now</span></>
                : <>Already a partner? <span onClick={() => { setMode('login'); setError(''); }}>Login</span></>}
            </p>
          )}

          {step === 'otp' && (
            <p className="res-form-toggle">
              <span onClick={() => { setStep('mobile'); setOtp(['','','','','','']); setDevOtp(''); setError(''); }}>← Change number</span>
            </p>
          )}
        </div> 
      </div>
    </div>
  );
};

export default ResellerAuth;